import type { ReactNode } from "react";
import { CalculationOutput } from "../types";
import { PeriodMode, fmtEUR, suffix } from "../utils/period";

export default function Results({
  output,
  periodMode,
}: {
  output: CalculationOutput | null;
  periodMode: PeriodMode;
}) {
  if (!output) return null;

  const bruto = (output as any).brutoFacturado ?? 0;
  const costes = (output as any).costesCash ?? 0;
  const reta = (output as any).cuotaRETAAnual ?? 0;
  const gastosYReta = costes + reta;
  const rendNeto = output.rendimientoNeto ?? 0;
  const baseIRPF = output.baseIRPF ?? rendNeto;
  const irpfTotal = (output as any).irpfTotal ?? output.irpf?.cuotaTotal ?? 0;
  const pctBase = (output as any).irpfPctSobreBaseIRPF ?? 0;
  const pctBruto = (output as any).irpfPctSobreBruto ?? 0;
  const netoFiscal = output.netoFiscalAnual ?? rendNeto - irpfTotal;
  const antRet = (output as any).anticiposIRPF_retenciones ?? 0;
  const ant130 = (output as any).anticiposIRPF_mod130 ?? 0;
  const anticipos = (output as any).anticiposIRPF ?? antRet + ant130;
  const reg = Number((output as any).irpfRegularizacion ?? 0);
  const pendiente = (output as any).irpfPendiente ?? Math.max(0, reg);
  const devolucion = (output as any).irpfDevolucion ?? Math.max(0, -reg);
  const caja = (output as any).cajaPreviaRenta ?? 0;
  const neto = (output as any).netoFinalTrasRenta ?? output.netoFiscalAnual ?? 0;
  const hijosCount = output.hijosCount ?? 0;
  const hijosMenores3Count = output.hijosMenores3Count ?? 0;

  const f = (annual: number) => `${fmtEUR(annual, periodMode)}${suffix(periodMode)}`;
  const pct = (value: number, valid: boolean) => (valid ? `${value.toFixed(1)}%` : "—");
  const posNegClass = (value: number) => (value > 0 ? "pos" : value < 0 ? "neg" : "");

  return (
    <section className="results-section">
      <ResultCard title="Ingresos y costes">
        <Row label="Bruto" value={f(bruto)} />
        <Row label="Costes" value={f(-costes)} tone="neg" />
        <Row label="RETA" value={f(-reta)} tone="neg" />
        <Row label="Gastos + RETA" value={f(-gastosYReta)} tone="neg" />
        <Row label="Rendimiento neto" value={f(rendNeto)} tone="pos" highlight />
      </ResultCard>

      <ResultCard title="IRPF (teórico vs. anticipos)">
        <Row label="Base imponible" value={f(baseIRPF)} />
        <Row label="IRPF total" value={f(-irpfTotal)} tone="neg" />
        <Row label="Tipo efectivo base" value={pct(pctBase, baseIRPF > 0)} />
        <Row label="IRPF/bruto" value={pct(pctBruto, bruto > 0)} />
        <Row label="Neto fiscal" value={f(netoFiscal)} tone="pos" highlight />
        <Row label="Anticipos retenciones" value={f(-antRet)} tone="neg" />
        <Row label="Anticipos mod.130" value={f(-ant130)} tone="neg" />
        <Row label="Anticipos total" value={f(-anticipos)} tone="neg" />
        <Row label="Regularización" value={f(reg)} tone={posNegClass(reg)} />
        {devolucion > 0 && <Row label="Devolución" value={f(devolucion)} tone="pos" />}
        {pendiente > 0 && <Row label="Pendiente" value={f(pendiente)} tone="neg" />}
      </ResultCard>

      <ResultCard title="Caja y neto">
        <Row
          label="Caja previa"
          value={f(caja)}
          title="Caja tras retenciones, pagos 130 e impacto anual de IVA antes de la renta."
        />
        <Row
          label="Neto final"
          value={f(neto)}
          tone="pos"
          highlight
          title="Caja final después de sumar devolución o restar la regularización del IRPF."
        />
      </ResultCard>

      {output.irpf && (
        <ResultCard title="Detalle IRPF">
          <div className="results-grid">
            <MiniMetric label="Cuota estatal" value={f(output.irpf.cuotaEstatal)} />
            <MiniMetric label="Cuota autonómica" value={f(output.irpf.cuotaAutonomica)} />
            <MiniMetric
              label="Mínimo personal y familiar"
              value={
                output.minimoPersonalFamiliar
                  ? f(output.minimoPersonalFamiliar)
                  : "—"
              }
              note={
                hijosCount > 0
                  ? `Descendientes: ${f(output.minimoHijos || 0)}${
                      hijosMenores3Count > 0
                        ? ` · incluye ${hijosMenores3Count} menor${hijosMenores3Count > 1 ? "es" : ""} de 3 años`
                        : ""
                    }`
                  : undefined
              }
            />
          </div>

          <div className="results-card-body">
            {output.irpf.deductions?.length ? (
              output.irpf.deductions.map((deduction, index) => (
                <div
                  key={`${deduction.label}-${index}`}
                  className="results-row"
                >
                  <span className="results-label">{deduction.label}</span>
                  <span className="results-value pos">-{f(deduction.amount)}</span>
                </div>
              ))
            ) : (
              <div className="results-row">
                <span className="results-label">No hay deducciones adicionales</span>
                <span className="results-value">—</span>
              </div>
            )}
          </div>
        </ResultCard>
      )}

      {output.quarterly && (
        <ResultCard title="Resumen trimestral">
          <div className="qtable-wrap">
            <table className="qtable">
              <thead>
                <tr>
                  <th>Trim.</th>
                  <th>Ingresos</th>
                  <th>Gastos ded.</th>
                  <th>IVA neto</th>
                  <th>Retenciones</th>
                  <th>Pagos 130</th>
                  <th>Anticipos IRPF</th>
                  <th>Neto caja</th>
                </tr>
              </thead>
              <tbody>
                {output.quarterly.qrows.map((row) => (
                  <tr key={row.quarter}>
                    <td>Q{row.quarter}</td>
                    <td>{fmtEUR(row.ingresosImponibles)}</td>
                    <td>{fmtEUR(row.gastosDeducibles)}</td>
                    <td>{fmtEUR(row.liquidacionIVA)}</td>
                    <td>{fmtEUR(row.retencionesSufridas)}</td>
                    <td>{fmtEUR(row.pagos130)}</td>
                    <td>{fmtEUR(row.irpfAnticipos)}</td>
                    <td>{fmtEUR(row.netoCaja)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </ResultCard>
      )}
    </section>
  );
}

function ResultCard({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <div className="results-card">
      <div className="results-card-hdr">
        <h2 className="results-card-title">{title}</h2>
      </div>
      {children}
    </div>
  );
}

function Row({
  label,
  value,
  tone,
  title,
  highlight = false,
}: {
  label: string;
  value: string;
  tone?: "" | "pos" | "neg";
  title?: string;
  highlight?: boolean;
}) {
  return (
    <div className={`results-row${highlight ? " highlight" : ""}`} title={title}>
      <span className="results-label">{label}</span>
      <span className={`results-value${tone ? ` ${tone}` : ""}`}>{value}</span>
    </div>
  );
}

function MiniMetric({
  label,
  value,
  note,
}: {
  label: string;
  value: string;
  note?: string;
}) {
  return (
    <div className="results-mini-card">
      <div className="results-mini-label">{label}</div>
      <div className="results-value" style={{ marginTop: 6 }}>
        {value}
      </div>
      {note ? <div className="results-mini-note">{note}</div> : null}
    </div>
  );
}
